import { SelectProps } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import Chip from "@material-ui/core/Chip";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles, Theme, useTheme } from "@material-ui/core/styles";
import React from "react";
import CustomDropdown from "./CustomDropdown";

const NO_OPTIONS_LABEL = "No options available";

type DropdownSelectable = {
  key: string;
  name: string;
};

const useStyles = makeStyles({
  chip: {
    height: "1.25rem",
    fontSize: "0.875rem",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    rowGap: "3px",
    "& > *": {
      marginRight: "0.5rem",
    },
  },
  optionCheckbox: {
    padding: 0,
    marginRight: "12px",
  },
  optionLabel: {
    alignItems: "center",
    display: "flex",
    whiteSpace: "pre",
  },
  "pb-adjust": {
    paddingBottom: "4.625px",
  },
});

interface CustomDropdownMultipleProps<OptionType extends string> extends SelectProps {
  chips?: boolean;
  labelText: string;
  helperText?: string;
  noOptionsLabel?: string;
  options: OptionType[];
  optionsCheckbox?: boolean;
  optionsDictionary?: { [O in OptionType]: string };
  value: OptionType[];
}

export function getStyles<T extends string | DropdownSelectable>(option: T, selected: T[], theme: Theme) {
  return {
    fontWeight: selected.indexOf(option) === -1 ? theme.typography.fontWeightRegular : theme.typography.fontWeightBold,
  };
}

function CustomDropdownMultiple<T extends string>({
  chips,
  fullWidth,
  helperText,
  labelText,
  noOptionsLabel = NO_OPTIONS_LABEL,
  options,
  optionsCheckbox,
  optionsDictionary,
  value,
  ...selectProps
}: CustomDropdownMultipleProps<T>) {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <CustomDropdown
      fullWidth={fullWidth}
      inputProps={{
        className: value.length && chips ? classes["pb-adjust"] : "",
      }}
      helperText={helperText}
      labelText={labelText}
      multiple
      renderValue={(selectedAll) =>
        chips ? (
          <div className={classes.chips}>
            {(selectedAll as typeof options).map(
              (selectedItem) =>
                selectedItem && (
                  <Chip
                    className={classes.chip}
                    key={selectedItem}
                    label={optionsDictionary?.[selectedItem] || selectedItem}
                  />
                ),
            )}
          </div>
        ) : (
          (selectedAll as typeof options).map((so) => optionsDictionary?.[so] || so).join(", ")
        )
      }
      value={value}
      {...selectProps}
    >
      {options.length ? (
        options.map((option) => (
          <MenuItem className={classes.optionLabel} key={option} value={option} style={getStyles(option, value, theme)}>
            {optionsCheckbox && (
              <Checkbox className={classes.optionCheckbox} size="small" checked={value.indexOf(option) > -1} />
            )}
            <span style={{ lineHeight: 1 }}>{optionsDictionary?.[option] || option}</span>
          </MenuItem>
        ))
      ) : (
        <MenuItem value="EMPTY_OPTION">{noOptionsLabel}</MenuItem>
      )}
    </CustomDropdown>
  );
}

export default CustomDropdownMultiple;
